import { useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  Download,
  FileSpreadsheet,
  History,
  RefreshCcw,
  TriangleAlert,
  Upload,
} from "lucide-react";
import { PageHead } from "@/components/Filters";
import { api, errMsg } from "@/lib/api";
import { UploadContextNotices } from "@/components/admin/UploadContextNotices";

const BODIES = ["KEA", "MCC"];
const MULTI_STATE = "Multiple states (from file)";
const STATES = [
  "Karnataka",
  "All India",
  "Maharashtra",
  "Tamil Nadu",
  "Telangana",
  "Kerala",
  "Gujarat",
  MULTI_STATE,
];
const ROUNDS = ["Round 1", "Round 2", "Round 3", "Round 4", "Mop-Up", "Stray Vacancy"];
const TYPES = [
  ["allotment", "Allotment data"],
  ["fees", "Fee structure"],
  ["college_details", "College details"],
];

export default function AdminUpload() {
  const [meta, setMeta] = useState({
    counselling_body: "KEA",
    state: "Karnataka",
    year: 2025,
    round: "Round 1",
    data_type: "allotment",
  });
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [batchBusy, setBatchBusy] = useState({});

  const set = (k) => (e) => setMeta((p) => ({ ...p, [k]: e.target.value }));
  const isAllotment = meta.data_type === "allotment";

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get(`/admin/upload-templates/${meta.data_type}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `NeetOne_${meta.data_type}_template.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const upload = async () => {
    if (!file) return toast.error("Choose a CSV or Excel file");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("counselling_body", meta.counselling_body);
      fd.append("state", meta.state);
      fd.append("year", meta.year);
      fd.append("data_type", meta.data_type);
      if (!isAllotment) fd.append("round", meta.round);
      const { data } = await api.post("/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      const total = data.batches?.length || 0;
      const problems = data.error_count || 0;
      if (data.status === "validated") {
        toast.success(
          `Parsed ${total} round${total === 1 ? "" : "s"} — review the preview, then publish.`
        );
      } else {
        toast.error(`Blocked: ${problems} error(s) across ${total} round${total === 1 ? "" : "s"}`);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const act = async (batchId, kind) => {
    setBatchBusy((p) => ({ ...p, [batchId]: kind }));
    try {
      let data;
      if (kind === "publish") {
        ({ data } = await api.post(`/admin/uploads/${batchId}/publish`));
        toast.success(`Published ${data.records} rows`);
        setResult((r) => ({
          ...r,
          batches: r.batches.map((b) =>
            b.batch_id === batchId ? { ...b, status: "published" } : b
          ),
        }));
      } else {
        ({ data } = await api.post(`/admin/uploads/${batchId}/revalidate`));
        toast[data.status === "validated" ? "success" : "error"](`Now ${data.status}`);
        setResult((r) => ({
          ...r,
          batches: r.batches.map((b) =>
            b.batch_id === batchId
              ? {
                  ...b,
                  status: data.status,
                  error_count: data.error_count,
                  errors: data.errors || [],
                  unmapped_codes: data.unmapped_codes || [],
                }
              : b
          ),
        }));
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBatchBusy((p) => {
        const n = { ...p };
        delete n[batchId];
        return n;
      });
    }
  };

  const publishAll = async () => {
    const batches = (result?.batches || []).filter((b) => b.status === "validated");
    for (const b of batches) {
      // eslint-disable-next-line no-await-in-loop
      await act(b.batch_id, "publish");
    }
  };

  const mapAlias = async (raw, canonical, batchId) => {
    try {
      await api.post("/admin/aliases", { entity_type: "college", raw, canonical });
      toast.success("Alias saved — re-validating");
      await act(batchId, "revalidate");
      setResult((r) => ({
        ...r,
        duplicate_suggestions: (r.duplicate_suggestions || []).filter((s) => s.raw !== raw),
      }));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const previewCols =
    isAllotment
      ? [
          "row", "row_round", "row_state", "college", "branch", "degree", "branch_clean",
          "raw_category", "quota_normalized", "category_normalized", "rank_value", "course_fee_value",
        ]
      : meta.data_type === "fees"
        ? ["row", "row_state", "college", "branch", "fee_quota", "tuition_fee_value", "stipend_value"]
        : ["row", "college", "city", "college_type", "beds", "established", "row_state"];

  const subtitle = isAllotment
    ? "One CSV can carry rows from multiple rounds — the Round column is read per row. Each round becomes its own batch and replaces any existing data for the same body / year / round."
    : "Pick a round and upload the fee or college-details sheet for that round.";

  return (
    <div className="space-y-5" data-testid="admin-upload-page">
      <PageHead title="Upload counselling data" subtitle={subtitle} />

      <div className="grid gap-4 border border-slate-200 bg-white p-4 lg:grid-cols-5">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Counselling body</span>
          <select
            data-testid="meta-counselling_body"
            value={meta.counselling_body}
            onChange={set("counselling_body")}
            className="h-9 border border-slate-300 px-2 text-sm"
          >
            {BODIES.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">State</span>
          <select
            data-testid="meta-state"
            value={meta.state}
            onChange={set("state")}
            className="h-9 border border-slate-300 px-2 text-sm"
          >
            {STATES.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>
        {!isAllotment && (
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Round</span>
            <select
              data-testid="meta-round"
              value={meta.round}
              onChange={set("round")}
              className="h-9 border border-slate-300 px-2 text-sm"
            >
              {ROUNDS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </label>
        )}
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Year</span>
          <input
            data-testid="meta-year"
            type="number"
            value={meta.year}
            onChange={set("year")}
            className="h-9 border border-slate-300 px-2 font-mono-num text-sm"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Data type</span>
          <select
            data-testid="meta-data_type"
            value={meta.data_type}
            onChange={set("data_type")}
            className="h-9 border border-slate-300 px-2 text-sm"
          >
            {TYPES.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </label>
      </div>

      <UploadContextNotices dataType={meta.data_type} isMultiState={meta.state === MULTI_STATE} />
      {isAllotment && (
        <p
          data-testid="allotment-multiround-notice"
          className="border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900"
        >
          <strong>Round is read per row.</strong> Each file can mix Round 1, Round 2, Mop-Up, Stray Vacancy
          etc. — one batch is created for each round found. Publishing a round <em>replaces</em> any
          existing published data for the same body / year / round.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-4 border border-dashed border-slate-300 bg-white p-6">
        <FileSpreadsheet className="h-6 w-6 text-slate-400" />
        <input
          data-testid="file-input"
          type="file"
          accept=".csv,.xls,.xlsx"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm"
        />
        <button
          data-testid="download-upload-template-btn"
          onClick={downloadTemplate}
          className="flex h-9 items-center gap-2 border border-slate-300 px-3 text-xs font-semibold uppercase tracking-widest text-slate-700 hover:border-[#1A365D] hover:text-[#1A365D]"
        >
          <Download className="h-3.5 w-3.5" />
          Download {TYPES.find(([v]) => v === meta.data_type)?.[1]} sample
        </button>
        <button
          data-testid="parse-upload-btn"
          disabled={busy}
          onClick={upload}
          className="ml-auto flex h-9 items-center gap-2 bg-[#1A365D] px-4 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#122845] disabled:opacity-40"
        >
          <Upload className="h-3.5 w-3.5" /> {busy ? "Working…" : "Parse & validate"}
        </button>
      </div>

      {isAllotment && (
        <p data-testid="allotment-quota-hint" className="mt-2 text-xs text-slate-500">
          <span className="font-semibold text-slate-700">Quota is optional.</span> Leave it blank to let
          NeetOne derive it from the Category code (KEA rule). MCC and other state files can supply it
          directly (e.g. "All India Quota", "Deemed / Paid Seats Quota").
        </p>
      )}

      {result && (
        <div className="space-y-4" data-testid="upload-result">
          <div
            className={`flex flex-wrap items-center gap-3 border px-4 py-3 text-sm ${
              result.status === "validated"
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : "border-rose-200 bg-rose-50 text-rose-900"
            }`}
            data-testid="upload-status-banner"
          >
            {result.status === "blocked" ? <TriangleAlert className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
            <span className="font-semibold uppercase tracking-widest">{result.status}</span>
            <span className="font-mono-num">
              {result.batches?.length || 0} round{(result.batches?.length || 0) === 1 ? "" : "s"} · {result.row_count || 0} rows · {result.error_count || 0} errors
            </span>
            {result.batches?.some((b) => b.status === "validated") && (
              <button
                data-testid="publish-all-btn"
                onClick={publishAll}
                className="ml-auto bg-[#1A365D] px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#122845]"
              >
                Publish all validated rounds
              </button>
            )}
            {!result.batches?.some((b) => b.status === "validated") && result.batches?.some((b) => b.status === "needs_review") && (
              <span className="ml-auto text-xs font-semibold text-amber-800">
                Open each round&apos;s Review tab to resolve references before publishing.
              </span>
            )}
          </div>

          {result.batches?.length > 0 && (
            <div
              className="border border-slate-200 bg-white"
              data-testid="per-round-summary"
            >
              <p className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                Rounds detected
              </p>
              <ul className="divide-y divide-slate-100">
                {result.batches.map((b) => (
                  <li
                    key={b.batch_id}
                    className="flex flex-wrap items-center gap-3 px-3 py-3"
                    data-testid={`round-card-${b.round}`}
                  >
                    <span className="min-w-[120px] text-sm font-semibold text-slate-900">{b.round}</span>
                    <span className="font-mono-num text-xs text-slate-500">
                      {b.row_count} rows · {b.error_count} errors
                    </span>
                    <span
                      className={`border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                        b.replaces_existing
                          ? "border-amber-300 bg-amber-50 text-amber-900"
                          : "border-emerald-300 bg-emerald-50 text-emerald-900"
                      }`}
                    >
                      {b.replaces_existing ? "Replacing existing" : "New"}
                    </span>
                    <span
                      className={`border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                        b.status === "validated"
                          ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                          : b.status === "published"
                          ? "border-sky-300 bg-sky-50 text-sky-900"
                          : b.status === "needs_review"
                          ? "border-amber-300 bg-amber-50 text-amber-900"
                          : "border-rose-300 bg-rose-50 text-rose-900"
                      }`}
                    >
                      {b.status === "needs_review" ? "needs review" : b.status}
                    </span>
                    {b.unresolved_counts && (b.unresolved_counts.colleges + b.unresolved_counts.courses + b.unresolved_counts.categories + b.unresolved_counts.quotas) > 0 && (
                      <span className="font-mono-num text-xs text-amber-700">
                        {b.unresolved_counts.colleges + b.unresolved_counts.courses + b.unresolved_counts.categories + b.unresolved_counts.quotas} to resolve
                      </span>
                    )}
                    {b.unmapped_codes?.length > 0 && (
                      <span className="font-mono-num text-xs text-rose-700">
                        unmapped: {b.unmapped_codes.join(", ")}
                      </span>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                      {b.status === "needs_review" && (
                        <a
                          data-testid={`review-${b.batch_id}`}
                          href={`/admin/batches/${b.batch_id}/review`}
                          className="flex items-center gap-1 border border-amber-500 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-500 hover:text-white"
                        >
                          Review ({b.unresolved_counts
                            ? b.unresolved_counts.colleges + b.unresolved_counts.courses + b.unresolved_counts.categories + b.unresolved_counts.quotas
                            : 0})
                        </a>
                      )}
                      {b.status === "blocked" && (
                        <button
                          data-testid={`revalidate-${b.batch_id}`}
                          onClick={() => act(b.batch_id, "revalidate")}
                          disabled={!!batchBusy[b.batch_id]}
                          className="flex items-center gap-1 border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-[#1A365D] disabled:opacity-40"
                        >
                          <RefreshCcw className="h-3 w-3" /> Re-validate
                        </button>
                      )}
                      {b.status === "validated" && (
                        <button
                          data-testid={`publish-${b.batch_id}`}
                          onClick={() => act(b.batch_id, "publish")}
                          disabled={!!batchBusy[b.batch_id]}
                          className="bg-[#1A365D] px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-white disabled:opacity-40"
                        >
                          Publish round
                        </button>
                      )}
                      {b.status === "published" && (
                        <span className="flex items-center gap-1 text-[11px] font-semibold text-sky-800">
                          <History className="h-3 w-3" /> Live
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.duplicate_suggestions?.length > 0 && (
            <div
              data-testid="duplicate-suggestions"
              className="border border-amber-300 bg-amber-50/60"
            >
              <p className="border-b border-amber-300 bg-amber-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-amber-900">
                Possible duplicate college names ({result.duplicate_suggestions.length}) — confirm to map,
                nothing merges automatically
              </p>
              <ul className="max-h-72 divide-y divide-amber-200 overflow-auto">
                {result.duplicate_suggestions.map((s) => (
                  <li key={s.raw} className="px-3 py-2.5 text-sm">
                    <p className="font-medium text-slate-900">{s.raw}</p>
                    <div className="mt-1.5 space-y-1">
                      {s.matches.map((m) => (
                        <div
                          key={m.name}
                          className="flex flex-wrap items-center gap-2 text-xs text-slate-700"
                        >
                          <span className="font-mono-num text-slate-500">
                            {Math.round(m.score * 100)}%
                          </span>
                          <span className="border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-500">
                            {m.source}
                          </span>
                          <span className="truncate">{m.name}</span>
                          <button
                            data-testid={`map-alias-${s.raw}`}
                            onClick={() =>
                              mapAlias(
                                s.raw,
                                m.name,
                                result.batches?.find((b) => b.status === "blocked")?.batch_id
                                  || result.batches?.[0]?.batch_id,
                              )
                            }
                            className="ml-auto shrink-0 border border-[#1A365D] px-2 py-0.5 text-[11px] font-semibold text-[#1A365D] hover:bg-[#1A365D] hover:text-white"
                          >
                            Map to this
                          </button>
                        </div>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.batches?.some((b) => b.errors?.length > 0) && (
            <div className="border border-slate-200 bg-white" data-testid="validation-errors">
              <p className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                Validation errors
              </p>
              <ul className="max-h-72 divide-y divide-slate-100 overflow-auto">
                {result.batches.flatMap((b) =>
                  (b.errors || []).map((e, i) => (
                    <li
                      key={`${b.batch_id}-${e.row ?? e.type}-${i}`}
                      className="px-3 py-2 text-sm text-slate-700"
                    >
                      <span className="mr-2 font-mono-num text-[11px] text-slate-500">[{b.round}]</span>
                      <span className="mr-2 font-mono-num text-xs text-rose-700">
                        {e.row ? `row ${e.row}` : e.type}
                      </span>
                      {e.message}
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}

          {result.preview?.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                Parsed preview — raw code and mapped values side by side
              </p>
              <div className="mt-2 max-h-80 overflow-auto border border-slate-200 bg-white">
                <table className="data-table" data-testid="preview-table">
                  <thead>
                    <tr>
                      {previewCols.map((c) => (
                        <th key={c}>{c.replace(/_/g, " ")}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.preview.map((r, i) => (
                      <tr key={`${r.row ?? i}|${r.college ?? ""}|${r.branch ?? ""}`}>
                        {previewCols.map((c) => (
                          <td key={c} className={c.includes("raw") ? "font-mono-num text-slate-500" : ""}>
                            {r[c] == null || r[c] === "" ? (
                              <span className="text-rose-600">—</span>
                            ) : (
                              String(r[c])
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Detected columns:{" "}
                <span className="font-mono-num">{result.detected_columns?.join(", ")}</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
